import { useEffect } from 'react'
import { MenuList, MenuItem, Menu, MenuButton, IconButton, HStack, chakra } from '@chakra-ui/react'
import cx from 'classnames'
import { NotificationTypeEnum } from 'configs/constants/enums/notification'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import colors from 'theme/colors.theme'
import { seenNotification } from 'API/notification'
import SvgIcon from 'components/SvgIcon'
import { EBreakPoint } from 'constants/theme'
import routes from 'routes'
import CommentNotification from './components/CommentNofication'
import DeleteCommonStepNotification from './components/DeleteCommonStepNotification'
import OrgInvitationNotification from './components/OrgInvitationNotification'
import UpdateCommonStepNotification from './components/UpdateCommonStepNotification'
import styles from './styles.module.scss'
dayjs.extend(relativeTime)

function NotificationBell() {
  const { notificationStore, authStore } = useStores()
  const { notifications, notificationCountForActiveProfile: countNotifications } = notificationStore
  const navigate = useNavigate()
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)

  useEffect(() => {
    if (authStore?.userDetail?.id) {
      notificationStore.fetchNotifications()
      notificationStore.aggregateCountNotifications()
    }
  }, [authStore?.userDetail?.id, notificationStore])

  return (
    <div className={styles.container}>
      <Menu>
        {countNotifications > 0 && <div className={styles.notificationCount}>{countNotifications}</div>}
        <MenuButton
          as={IconButton}
          onClick={() => notificationStore.fetchNotifications()}
          aria-label="Notifications"
          background="transparent"
          _hover={{ background: 'transparent' }}
          _active={{ background: 'transparent' }}
          border="none"
          icon={<SvgIcon iconName="notification-bell" size={isMobile ? 24 : 20} />}
          variant="outline"
        />

        <MenuList className={styles.menu}>
          <HStack
            className={styles.header}
            justifyContent="space-between"
            padding="12px 16px"
            borderBottom="1px solid #E2E8F0"
          >
            <div className={styles.title}>Notifications</div>
            <chakra.div
              onClick={async () => {
                await notificationStore.seenAllNotifications()
                toast.success('Marked all as read')
                notificationStore.fetchNotifications()
                notificationStore.aggregateCountNotifications()
              }}
              className={styles.mark}
              color="var(--current-primary-color)"
              _hover={{
                color: 'var(--current-primary-color)' ?? 'primary.700',
                opacity: 0.8
              }}
            >
              Mark all as read
            </chakra.div>
          </HStack>
          <div className={styles.listNotifications}>
            {notifications.map((notification, index) => (
              <MenuItem
                key={index}
                background="transparent"
                border="none"
                _hover={{ background: colors.primary[50] }}
                className={cx({ [styles.unseen]: !notification.isSeen })}
              >
                <HStack
                  className={cx(styles.notificationItem)}
                  key={notification.id}
                  onClick={() => {
                    // ORG_INVITATION rows own their own actions (Accept / Deny buttons).
                    // Don't navigate to /notifications on row-click; just mark seen.
                    if (notification?.type !== NotificationTypeEnum.ORG_INVITATION) {
                      navigate(routes.notifications.value)
                    }
                    seenNotification(notification?.id ?? 0)
                  }}
                >
                  <HStack className={styles.notificationContent}>
                    {notification?.type === NotificationTypeEnum.COMMENT_NOTIFICATION && (
                      <CommentNotification notification={notification} />
                    )}
                    {notification?.type === NotificationTypeEnum.UPDATED_COMMON_STEP_NOTIFICATION && (
                      <UpdateCommonStepNotification notification={notification} />
                    )}
                    {notification?.type === NotificationTypeEnum.DELETED_COMMON_STEP_NOTIFICATION && (
                      <DeleteCommonStepNotification notification={notification} />
                    )}
                    {notification?.type === NotificationTypeEnum.ORG_INVITATION && (
                      <OrgInvitationNotification notification={notification} />
                    )}
                  </HStack>
                  <HStack>
                    <span className={styles.timestamp}>{dayjs(notification?.createdAt).fromNow(true)}</span>
                  </HStack>
                </HStack>
              </MenuItem>
            ))}
          </div>
        </MenuList>
      </Menu>
    </div>
  )
}

export default observer(NotificationBell)
